import { useState, useCallback, useMemo } from 'react';
import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { Breadcrumbs } from '../../components/products/Breadcrumbs';
import { AddPackButton } from '../../components/packs/AddPackButton';
import { Toast } from '../../components/packs/Toast';
import { useCartPacks } from '../../hooks/useCartPacks';
import { useAddPack } from '../../hooks/useAddPack';
import { route } from '../../utils/route';
import type { ProductPack, ProductPackItem, ProductPackChoiceGroup } from '../../types/packs';

function formatPrice(price: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

interface SolutionShowProps {
  pack: ProductPack;
  otherPacks: ProductPack[];
  appUrl: string;
}

export default function SolutionShow({ pack, otherPacks, appUrl }: SolutionShowProps) {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const hasChoices = pack.has_choice_groups && pack.choice_groups.length > 0;

  const [selections, setSelections] = useState<Record<number, number[]>>({});

  const { isPackInCart, addPackTracking, removePackTracking, refreshCart } = useCartPacks();

  const showSuccessToast = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
  }, []);

  const { addPack, addPackWithSelections, removePack, loadingPackId } = useAddPack({
    onSuccess: (p, itemKeys) => {
      addPackTracking(p, itemKeys);
      showSuccessToast(`"${p.title}" agregado al carrito`);
    },
    onRemoveSuccess: (packId) => {
      removePackTracking(packId);
      showSuccessToast('Solución removida del carrito');
    },
    refreshCart,
    source: 'solution',
  });

  const selectedItems = useMemo(() => {
    if (!hasChoices) return [];
    const items: ProductPackItem[] = [];
    for (const group of pack.choice_groups) {
      const selectedFormats = selections[group.id] ?? [];
      for (const formatId of selectedFormats) {
        const item = group.items.find(i => i.product_format_id === formatId);
        if (item) items.push(item);
      }
    }
    return items;
  }, [hasChoices, pack.choice_groups, selections]);

  const fixedTotal = useMemo(
    () => pack.items.reduce((sum, item) => sum + item.subtotal, 0),
    [pack.items],
  );
  const selectedTotal = useMemo(
    () => selectedItems.reduce((sum, item) => sum + item.subtotal, 0),
    [selectedItems],
  );
  const dynamicPrice = fixedTotal + selectedTotal;
  const hasAnySelection = selectedItems.length > 0;
  const showFromPrice = hasChoices && !hasAnySelection;

  const handleToggleOption = useCallback((group: ProductPackChoiceGroup, formatId: number) => {
    setSelections(prev => {
      const current = prev[group.id] ?? [];
      if (group.multiple) {
        const next = current.includes(formatId)
          ? current.filter(id => id !== formatId)
          : [...current, formatId];
        return { ...prev, [group.id]: next };
      } else {
        const next = current.includes(formatId) ? [] : [formatId];
        return { ...prev, [group.id]: next };
      }
    });
  }, []);

  const handleAdd = useCallback(async () => {
    try {
      if (hasChoices && selectedItems.length > 0) {
        await addPackWithSelections(pack, selectedItems);
      } else {
        await addPack(pack);
      }
    } catch {}
  }, [addPack, addPackWithSelections, pack, hasChoices, selectedItems]);

  const handleRemove = useCallback(async () => {
    try { await removePack(pack); } catch {}
  }, [removePack, pack]);

  const isInCart = isPackInCart(pack);
  const isLoading = loadingPackId === pack.id;

  const pageTitle = pack.meta_title
    ? `${pack.meta_title} | Abasto Virtual`
    : `${pack.title} | Soluciones Gastronómicas | Abasto Virtual`;
  const pageDescription = pack.description ||
    `Solución con ${pack.items_count} productos seleccionados. ${pack.items.slice(0, 5).map(i => i.product_title).join(', ')}.`;
  const pageUrl = `${appUrl}${route('solutions.show', { id: pack.slug || pack.id })}`;
  const pageImage = pack.image_url || `${appUrl}/appicons/og-image.jpg`;

  const breadcrumbSchema = useMemo(() => JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": `${appUrl}${route('index')}` },
      { "@type": "ListItem", "position": 2, "name": "Soluciones", "item": `${appUrl}${route('solutions.index')}` },
      { "@type": "ListItem", "position": 3, "name": pack.title, "item": pageUrl },
    ]
  }), [pack.title, pageUrl, appUrl]);

  const productSchema = useMemo(() => JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": pack.title,
    "description": pageDescription,
    ...(pack.image_url && { "image": pack.image_url }),
    "brand": { "@type": "Brand", "name": "Abasto Virtual" },
    "offers": {
      "@type": "Offer",
      "url": pageUrl,
      "priceCurrency": "ARS",
      "price": pack.calculated_price,
      "availability": "https://schema.org/InStock",
      "seller": { "@type": "Organization", "name": "Abasto Virtual" }
    },
    "category": "Soluciones Gastronómicas",
    "numberOfItems": pack.items_count,
  }), [pack, pageUrl, pageDescription]);

  return (
    <MainLayout>
      <Head>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription.substring(0, 160)} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={`${pack.title} - Solución Gastronómica`} />
        <meta property="og:description" content={pageDescription.substring(0, 200)} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:locale" content="es_AR" />
        <meta property="og:site_name" content="Abasto Virtual" />
        <meta property="product:price:amount" content={String(pack.calculated_price)} />
        <meta property="product:price:currency" content="ARS" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={pack.title} />
        <meta property="twitter:description" content={pageDescription.substring(0, 200)} />
        <meta property="twitter:image" content={pageImage} />

        <script type="application/ld+json">{breadcrumbSchema}</script>
        <script type="application/ld+json">{productSchema}</script>
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <Breadcrumbs
          items={[
            { name: 'Inicio', url: route('index') },
            { name: 'Soluciones', url: route('solutions.index') },
          ]}
          currentPage={pack.title}
        />

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col md:flex-row">
            <div className="h-[250px] w-full flex-shrink-0 bg-gray-100 md:h-[350px] md:w-[400px]">
              {pack.image_url ? (
                <img
                  src={pack.image_url}
                  alt={pack.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-6xl text-gray-300">
                  <i className="fa fa-box" />
                </div>
              )}
            </div>

            <div className="flex-1 p-5 md:p-8">
              <h1 className="m-0 mb-1 text-2xl font-bold text-gray-800 md:text-3xl">
                {pack.title}
              </h1>
              {pack.subtitle && (
                <p className="m-0 mb-3 text-lg text-gray-500">{pack.subtitle}</p>
              )}
              {!pack.subtitle && <div className="mb-2" />}

              <div className="mb-4 text-3xl font-bold text-green-600 md:text-4xl">
                {showFromPrice && (
                  <span className="mr-1 text-lg font-normal text-gray-500">Desde</span>
                )}
                {formatPrice(hasChoices ? dynamicPrice : pack.calculated_price)}
              </div>

              {pack.description && (
                <p className="mb-4 text-[15px] leading-relaxed text-gray-600">
                  {pack.description}
                </p>
              )}

              {pack.features && pack.features.length > 0 && (
                <div className="mb-5 flex flex-wrap gap-2">
                  {pack.features.map((feature, index) => (
                    <span
                      key={index}
                      className="inline-block rounded-full bg-green-100 px-3 py-1.5 text-[13px] font-medium text-green-800"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              )}

              <div className="mb-5 rounded-md bg-gray-50 p-3">
                <p className="m-0 mb-1.5 flex items-start gap-2 text-[13px] leading-snug text-gray-500">
                  <i className="fa fa-info-circle mt-0.5 flex-shrink-0 text-xs text-gray-400" />
                  Las imágenes son ilustrativas y generadas con IA. Los productos y el packaging pueden variar.
                </p>
                <p className="m-0 flex items-start gap-2 text-[13px] leading-snug text-gray-500">
                  <i className="fa fa-shopping-cart mt-0.5 flex-shrink-0 text-xs text-gray-400" />
                  Una vez agregado, podés modificar cantidades o eliminar productos del carrito de forma individual.
                </p>
              </div>

              <div className="w-full md:w-auto md:max-w-[300px]">
                <AddPackButton
                  isInCart={isInCart}
                  isLoading={isLoading}
                  onAdd={handleAdd}
                  onRemove={handleRemove}
                />
              </div>
            </div>
          </div>

          {/* Choice groups */}
          {hasChoices && (
            <div className="border-t border-gray-200 p-5 md:p-8">
              {pack.choice_groups.map((group) => {
                const groupSelections = selections[group.id] ?? [];
                return (
                  <div key={group.id} className="mb-6 last:mb-0">
                    <h3 className="m-0 mb-3 text-base font-semibold text-gray-800">
                      {group.label}
                      {!group.multiple && (
                        <span className="ml-2 text-xs font-normal text-gray-400">(elegí una opción)</span>
                      )}
                      {group.multiple && (
                        <span className="ml-2 text-xs font-normal text-gray-400">(podés elegir varias)</span>
                      )}
                    </h3>
                    <div className="flex flex-col gap-2">
                      {group.items.map((item) => {
                        const isSelected = groupSelections.includes(item.product_format_id);
                        return (
                          <button
                            key={`${item.product_id}-${item.product_format_id}`}
                            type="button"
                            onClick={() => handleToggleOption(group, item.product_format_id)}
                            className={`flex items-center justify-between rounded-lg border-2 px-4 py-3 text-left transition-all ${
                              isSelected
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                                isSelected ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300'
                              }`}>
                                {isSelected && (
                                  <i className="fa fa-check text-[10px]" />
                                )}
                              </span>
                              <div>
                                <span className="text-sm font-medium text-gray-800">
                                  {item.quantity}x {item.product_title}
                                </span>
                                {item.format_name && (
                                  <span className="ml-1 text-xs text-gray-500">({item.format_name})</span>
                                )}
                              </div>
                            </div>
                            <span className={`whitespace-nowrap text-sm font-medium ${
                              isSelected ? 'text-green-600' : 'text-gray-500'
                            }`}>
                              +{formatPrice(item.subtotal)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Products list */}
          <div className="border-t border-gray-200 p-5 md:p-8">
            <h2 className="m-0 mb-4 text-lg font-semibold text-gray-800">
              Productos incluidos ({pack.items.length}{hasChoices && selectedItems.length > 0 ? ` + ${selectedItems.length}` : ''})
            </h2>
            <div className="rounded-lg bg-gray-50 p-3">
              {pack.items.map((item, index) => (
                <div
                  key={`${item.product_id}-${item.product_format_id}`}
                  className={`flex items-center gap-3 py-2.5 ${
                    index !== pack.items.length - 1 || selectedItems.length > 0 ? 'border-b border-gray-200' : ''
                  } ${index === 0 ? 'pt-0' : ''}`}
                >
                  <div className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white">
                    {item.product_image_url ? (
                      <img src={item.product_image_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-300">
                        <i className="fa fa-leaf text-xs" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 items-center justify-between gap-2">
                    <div>
                      <span className="mr-1.5 font-semibold text-green-600">{item.quantity}x</span>
                      <span className="text-sm text-gray-800">{item.product_title}</span>
                      {item.format_name && (
                        <span className="ml-1 text-[13px] text-gray-500">({item.format_name})</span>
                      )}
                    </div>
                    <span className="whitespace-nowrap text-sm text-gray-600">
                      {formatPrice(item.subtotal)}
                    </span>
                  </div>
                </div>
              ))}
              {selectedItems.map((item, index) => (
                <div
                  key={`sel-${item.product_id}-${item.product_format_id}`}
                  className={`flex items-center gap-3 py-2.5 ${
                    index !== selectedItems.length - 1 ? 'border-b border-gray-200' : ''
                  } ${index === selectedItems.length - 1 ? 'pb-0' : ''}`}
                >
                  <div className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white">
                    {item.product_image_url ? (
                      <img src={item.product_image_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-300">
                        <i className="fa fa-leaf text-xs" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 items-center justify-between gap-2">
                    <div>
                      <span className="mr-1.5 font-semibold text-green-600">{item.quantity}x</span>
                      <span className="text-sm text-gray-800">{item.product_title}</span>
                      {item.format_name && (
                        <span className="ml-1 text-[13px] text-gray-500">({item.format_name})</span>
                      )}
                      <span className="ml-1.5 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                        elegido
                      </span>
                    </div>
                    <span className="whitespace-nowrap text-sm text-gray-600">
                      {formatPrice(item.subtotal)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related solutions */}
        {otherPacks.length > 0 && (
          <div className="mt-10 pb-10">
            <h2 className="m-0 mb-5 text-xl font-semibold text-gray-800">
              Otras soluciones que te pueden interesar
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {otherPacks.map((otherPack) => (
                <Link
                  key={otherPack.id}
                  href={route('solutions.show', { id: otherPack.slug || otherPack.id })}
                  className="flex overflow-hidden rounded-lg border border-gray-200 bg-white no-underline transition-shadow hover:shadow-md md:flex-col"
                >
                  <div className="h-[100px] w-[100px] flex-shrink-0 bg-gray-100 md:h-[150px] md:w-full">
                    {otherPack.image_url ? (
                      <img
                        src={otherPack.thumb_url || otherPack.image_url}
                        alt={otherPack.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-3xl text-gray-300">
                        <i className="fa fa-box" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col justify-center p-3">
                    <h3 className="m-0 mb-1 text-[15px] font-semibold text-gray-800">
                      {otherPack.title}
                    </h3>
                    <span className="text-base font-semibold text-green-600">
                      {otherPack.has_choice_groups && (
                        <span className="mr-1 text-xs font-normal text-gray-400">Desde</span>
                      )}
                      {formatPrice(otherPack.calculated_price)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {showToast && (
          <Toast message={toastMessage} onClose={() => setShowToast(false)} />
        )}
      </div>
    </MainLayout>
  );
}
